/**
 * Drop-in replacement for next/image in the Vite SPA.
 *
 * Handles the `priority` prop (converts to fetchPriority="high" / loading="eager")
 * so callers do not need to change their props. All other props pass through to
 * the native <img> element.
 */

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "loading"> & {
  /** Maps to fetchPriority="high" + loading="eager". Mirrors next/image's priority prop. */
  priority?: boolean;
  /** Maps directly to <img loading> when priority is false. Defaults to "lazy". */
  loading?: "lazy" | "eager";
  /** next/image compat — ignored in the SPA (no server-side resizing). */
  fill?: boolean;
  /** next/image compat — ignored in the SPA. */
  sizes?: string;
  /** next/image compat — ignored in the SPA. */
  placeholder?: "blur" | "empty";
  /** next/image compat — ignored in the SPA. */
  blurDataURL?: string;
  /** next/image compat — ignored in the SPA. */
  quality?: number;
};

export function Img({
  priority,
  loading,
  fill: _fill,
  sizes: _sizes,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  quality: _quality,
  ...rest
}: ImgProps) {
  const loadingAttr = priority ? "eager" : (loading ?? "lazy");

  return (
    <img
      {...rest}
      {...(priority ? { fetchPriority: "high" as const } : {})}
      loading={loadingAttr}
    />
  );
}
